import { useEffect, useState } from "react";
import styled from "styled-components";
import * as amplitude from "@amplitude/analytics-browser";
import { CDN_ROOT } from "./images";
import { Gender } from "../quiz/gender";
import { User } from "../types";
import { useSectionInView } from "./useSectionInView";

const BWS_BLUE = "#0B6DFF";
const BWS_BLACK = "#1A1A1A";
const BWS_GRAY_900 = "#1E1E1E";
const BWS_GRAY_700 = "#4A4A4A";
const BWS_GRAY_500 = "#7A7A7A";
const BWS_GRAY_BORDER = "#E9E9EF";
const BWS_BG_LIGHT = "#F5F5F5";
const WEB24_ASSET_ROOT = `${CDN_ROOT}/images/web24`;

const Section = styled.section`
  width: 100%;
  box-sizing: border-box;
  padding: 40px 20px;
  background-color: ${BWS_BG_LIGHT};

  @media (min-width: 768px) {
    padding: 64px 24px;
  }
`;

const SectionInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
`;

const SectionHeading = styled.h2`
  font-family: "Oswald", sans-serif;
  font-weight: 600;
  font-size: 24px;
  line-height: 1.2;
  color: ${BWS_GRAY_900};
  text-align: center;
  margin: 0 0 24px;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  @media (min-width: 768px) {
    font-size: 36px;
    margin-bottom: 40px;
  }
`;

const TestimonialGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  @media (min-width: 900px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 28px;
  }
`;

const TestimonialCard = styled.article`
  background: #fff;
  border-radius: 16px;
  padding: 16px 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.06);
`;

const BeforeAfterWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 12px;
  overflow: hidden;
  background: #e5e5e5;
`;

const BeforeAfterImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const BeforeLabel = styled.div`
  position: absolute;
  left: 0;
  bottom: 0;
  width: 50%;
  padding: 10px 0;
  background: ${BWS_BLUE};
  color: #fff;
  font-family: "Oswald", sans-serif;
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 1px;
  text-align: center;
  text-transform: uppercase;

  @media (min-width: 768px) {
    font-size: 16px;
    padding: 12px 0;
  }
`;

const AfterLabel = styled(BeforeLabel)`
  left: auto;
  right: 0;
  background: #111;
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0 4px;
`;

const NameRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const Name = styled.h3`
  font-family: "Oswald", sans-serif;
  font-weight: 600;
  font-size: 28px;
  line-height: 1;
  color: ${BWS_GRAY_900};
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  @media (min-width: 768px) {
    font-size: 32px;
  }
`;

const QuoteMark = styled.svg`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  color: ${BWS_BLUE};
`;

const Headline = styled.p`
  font-family: "Lato", sans-serif;
  font-weight: 800;
  font-size: 16px;
  line-height: 1.3;
  color: ${BWS_GRAY_900};
  margin: 0;

  @media (min-width: 768px) {
    font-size: 18px;
  }
`;

const QuoteText = styled.p`
  font-family: "Lato", sans-serif;
  font-weight: 400;
  font-size: 15px;
  line-height: 1.55;
  color: ${BWS_GRAY_700};
  margin: 0;

  @media (min-width: 768px) {
    font-size: 16px;
  }
`;

// ---------- As Featured In ----------

const PressSection = styled.section`
  width: 100%;
  box-sizing: border-box;
  padding: 40px 20px;
  background: #fff;
  border-top: 1px solid ${BWS_GRAY_BORDER};
  border-bottom: 1px solid ${BWS_GRAY_BORDER};

  @media (min-width: 768px) {
    padding: 56px 24px;
  }
`;

const PressLabel = styled.p`
  font-family: "Oswald", sans-serif;
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: ${BWS_GRAY_500};
  text-align: center;
  margin: 0 0 24px;

  @media (min-width: 768px) {
    font-size: 16px;
    margin-bottom: 32px;
  }
`;

const PressRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  align-items: center;
  justify-items: center;
  gap: 16px;
  max-width: 900px;
  margin: 0 auto;

  @media (min-width: 768px) {
    gap: 40px;
  }
`;

const PressLogo = styled.img`
  max-width: 100%;
  max-height: 32px;
  width: auto;
  height: auto;
  object-fit: contain;
  filter: grayscale(1);
  opacity: 0.7;

  @media (min-width: 768px) {
    max-height: 48px;
  }
`;

// ---------- FAQ ----------

const FAQList = styled.div`
  max-width: 720px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FAQItem = styled.div`
  background: #fff;
  border: 1px solid ${BWS_GRAY_BORDER};
  border-radius: 12px;
  overflow: hidden;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &[data-open="true"] {
    border-color: ${BWS_BLUE};
    box-shadow: 0 4px 16px rgba(11, 109, 255, 0.08);
  }
`;

const FAQButton = styled.button`
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  cursor: pointer;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  font-family: "Lato", sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 1.4;
  color: ${BWS_GRAY_900};

  @media (min-width: 768px) {
    font-size: 18px;
    padding: 22px 24px;
  }
`;

const FAQIcon = styled.span<{ $open: boolean }>`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${BWS_BLUE};
  font-size: 24px;
  line-height: 1;
  transition: transform 0.2s ease;
  transform: rotate(${(p) => (p.$open ? "45deg" : "0deg")});
`;

const FAQAnswer = styled.div<{ $open: boolean }>`
  max-height: ${(p) => (p.$open ? "500px" : "0")};
  overflow: hidden;
  transition: max-height 0.25s ease, padding 0.25s ease;
  padding: ${(p) => (p.$open ? "0 20px 20px" : "0 20px")};

  font-family: "Lato", sans-serif;
  font-weight: 400;
  font-size: 15px;
  line-height: 1.6;
  color: ${BWS_GRAY_700};

  @media (min-width: 768px) {
    font-size: 16px;
    padding: ${(p) => (p.$open ? "0 24px 24px" : "0 24px")};
  }
`;

// ---------- Reviews ----------

const ReviewsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  @media (min-width: 900px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
  }
`;

const ReviewCard = styled.div`
  position: relative;
  background: #fff;
  border: 1px solid ${BWS_GRAY_BORDER};
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  box-shadow: 0 8px 24px rgba(17, 24, 39, 0.05);
  overflow: hidden;
`;

const CardWatermark = styled.svg`
  position: absolute;
  top: -12px;
  right: -12px;
  width: 120px;
  height: 120px;
  color: ${BWS_BLUE};
  opacity: 0.06;
  pointer-events: none;
`;

const StarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Stars = styled.div`
  display: flex;
  gap: 2px;
`;

const Star = styled.svg`
  width: 18px;
  height: 18px;
  color: #f5b700;
  flex-shrink: 0;
`;

const ReviewRating = styled.span`
  font-family: "Lato", sans-serif;
  font-weight: 700;
  font-size: 13px;
  color: ${BWS_GRAY_500};
  letter-spacing: 0.3px;
`;

const ReviewTitle = styled.p`
  font-family: "Lato", sans-serif;
  font-weight: 800;
  font-size: 17px;
  line-height: 1.3;
  color: ${BWS_GRAY_900};
  margin: 0;

  @media (min-width: 768px) {
    font-size: 19px;
  }
`;

const ReviewBody = styled.p`
  font-family: "Lato", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.6;
  color: ${BWS_GRAY_700};
  margin: 0;

  @media (min-width: 768px) {
    font-size: 15px;
  }
`;

const ReviewAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid ${BWS_GRAY_BORDER};
`;

const Avatar = styled.div<{ $color: string }>`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  color: #fff;
  font-family: "Lato", sans-serif;
  font-weight: 800;
  font-size: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: 0.5px;
`;

const AuthorMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const AuthorName = styled.span`
  font-family: "Lato", sans-serif;
  font-weight: 800;
  font-size: 14px;
  color: ${BWS_GRAY_900};
  line-height: 1.2;
`;

const AuthorBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: "Lato", sans-serif;
  font-weight: 600;
  font-size: 12px;
  color: ${BWS_BLUE};
  line-height: 1.2;
`;

const VerifiedTick = styled.svg`
  width: 14px;
  height: 14px;
  flex-shrink: 0;
`;

// ---------- Data ----------

type Testimonial = {
  id: string;
  name: string;
  image: string;
  headline: string;
  quote: string;
  gender: Gender;
};

const MALE_TESTIMONIALS: Testimonial[] = [
  {
    id: "romeo",
    name: "Romeo F.",
    image: `${WEB24_ASSET_ROOT}/romeo.png`,
    headline: "Dropped from 25% to 9% body fat while doubling my bench strength",
    quote:
      "\u201CThis program completely changed my life. I didn\u2019t realize how many years I wasted eating what I thought was healthy and training without a real plan. I went from 29% body fat to 9% and doubled my bench press. It is worth every penny and more.\u201D",
    gender: Gender.Male,
  },
  {
    id: "mateus",
    name: "Mateus H.",
    image: `${WEB24_ASSET_ROOT}/mateus.png`,
    headline: "40 lbs down in 3 months",
    quote:
      "\u201CI went up to 263 pounds and didn\u2019t even realize how bad it had gotten. Once I actually followed the nutrition targets in my program, my body changed fast. I lost 40 pounds in three months, while getting stronger on my lifts. My only regret is not finding this program sooner.\u201D",
    gender: Gender.Male,
  },
  {
    id: "ron",
    name: "Ron I.",
    image: `${WEB24_ASSET_ROOT}/ron.png`,
    headline: "58 years old and in the BEST shape of my life",
    quote:
      "\u201CI\u2019m 58 with a desk job. I dropped 25 pounds, went from over 25% body fat to around 12%, and I can do more pull-ups now than I could in my 30s. Every time I tried to lose weight before, I\u2019d lose strength too. This program showed me how to keep the muscle and just lose the fat.\u201D",
    gender: Gender.Male,
  },
];

const FEMALE_TESTIMONIALS: Testimonial[] = [
  {
    id: "tamara",
    name: "Tamara M.",
    image: `${WEB24_ASSET_ROOT}/tamara.png`,
    headline: "Down 34 pounds and stronger than ever",
    quote:
      "\u201CI\u2019d start a program, last maybe two months, get discouraged, and quit. This was the first one that actually stuck. I went from 159 to 125 pounds and I couldn\u2019t even do a push up when I started. Now I\u2019m up to eight. My life has completely changed.\u201D",
    gender: Gender.Female,
  },
  {
    id: "danielle",
    name: "Danielle S.",
    image: `${WEB24_ASSET_ROOT}/danielle.png`,
    headline: "40% to 25% body fat. No crash diets.",
    quote:
      "\u201CI used to restrict, crash, and start all over again. This program helped me stop thinking of food as all-or-nothing. I have default meals now, I\u2019m never starving, and I dropped from 40% body fat to 25%. I wish I\u2019d stopped trying to be perfect and just started sooner.\u201D",
    gender: Gender.Female,
  },
  {
    id: "christen",
    name: "Christen M.",
    image: `${WEB24_ASSET_ROOT}/christen.png`,
    headline: "First program that actually helped me stay consistent",
    quote:
      "\u201CI finally lost the belly, the love handles, and achieved my goal of being able to do pull-ups. Before this, I was hopping between programs and restarting every Monday, but this is the first one that actually helped me stay consistent and see real results.\u201D",
    gender: Gender.Female,
  },
];

const MIXED_TESTIMONIALS: Testimonial[] = [
  MALE_TESTIMONIALS[0],
  FEMALE_TESTIMONIALS[2],
  MALE_TESTIMONIALS[1],
];

function getTestimonialsForGender(gender: Gender | undefined): Testimonial[] {
  if (gender === Gender.Male) return MALE_TESTIMONIALS;
  if (gender === Gender.Female) return FEMALE_TESTIMONIALS;
  return MIXED_TESTIMONIALS;
}

const PRESS_LOGOS = [
  { src: `${CDN_ROOT}/images/mensHealth.jpg`, alt: "Men's Health" },
  { src: `${CDN_ROOT}/images/yahoo.jpg`, alt: "Yahoo!" },
  { src: `${CDN_ROOT}/images/ktla.webp`, alt: "KTLA5" },
  { src: `${CDN_ROOT}/images/womensFitness.jpg`, alt: "Women's Fitness" },
];

const FAQS = [
  {
    q: "How does the 14-day free trial work?",
    a: "You get full access to your personalized plan for 14 days. Two days before your trial expires, we\u2019ll remind you so there are no hidden surprises. If it\u2019s not for you, cancel anytime with one tap. No questions asked.",
  },
  {
    q: "How is this different from other fitness programs?",
    a: "Most programs only give you one piece of the puzzle: workouts or nutrition. BWS+ brings your training, nutrition, and coaching together in one place. Everything is built on peer-reviewed research and adapts as your body changes. No more guessing, no more restarting, and no more being stuck with the same plan you got on day one.",
  },
  {
    q: "What if I don\u2019t see results?",
    a: "You have a full 14 days to try your personalized program completely free. 83% of members experience noticeable transformations in 2 months or less and our plans adapt as you progress so you\u2019re never stuck doing something that isn\u2019t working.",
  },
];

const REVIEWS = [
  {
    title: "Down 21 lbs and stronger than ever!",
    body:
      "Three months in I\u2019m down 21 lbs and hitting PRs I couldn\u2019t touch before. I always assumed losing fat meant losing strength too. This is the first program where I actually looked better AND lifted heavier at the end. Wish I\u2019d found it sooner.",
    author: "Jake R.",
    initials: "JR",
    accent: "#0B6DFF",
  },
  {
    title: "Literally everything you need all-in-one.",
    body:
      "I\u2019ve tried so many programs in the past that promised to be complete but none were. This one covers your workouts, nutrition, and coaches you all in one place. I\u2019ve seen better results in the past 2 months than the past 2 years trying to do it on my own!",
    author: "Priya S.",
    initials: "PS",
    accent: "#1E3A8A",
  },
  {
    title: "I can\u2019t believe my eyes",
    body:
      "It\u2019s only been 3 weeks and I\u2019m down 8 lbs and my shirts already fit different. I love how everything is backed by science and actually adapts as your body changes. You don\u2019t have to be perfect, you just have to show up and it\u2019ll do the rest.",
    author: "Marcus T.",
    initials: "MT",
    accent: "#0B6DFF",
  },
];

// ---------- Component ----------

const QuoteIcon = () => (
  <QuoteMark viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M7.17 6C4.87 6 3 7.87 3 10.17v6.66c0 .64.52 1.17 1.17 1.17h4.66c.64 0 1.17-.53 1.17-1.17V12.5c0-.64-.53-1.17-1.17-1.17H6.33a3.33 3.33 0 0 1 2.17-2.72c.55-.2.83-.8.64-1.35-.17-.51-.7-.81-1.25-.74A5.5 5.5 0 0 0 7.17 6Zm10 0c-2.3 0-4.17 1.87-4.17 4.17v6.66c0 .64.52 1.17 1.17 1.17h4.66c.64 0 1.17-.53 1.17-1.17V12.5c0-.64-.53-1.17-1.17-1.17h-2.5a3.33 3.33 0 0 1 2.17-2.72c.55-.2.83-.8.64-1.35-.17-.51-.7-.81-1.25-.74a5.5 5.5 0 0 0-.72.08Z" />
  </QuoteMark>
);

const FAQItemView = ({ q, a, index }: { q: string; a: string; index: number }) => {
  const [open, setOpen] = useState(false);
  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      amplitude.track("web24_faq_opened", { faq_index: index, question: q });
    }
  };
  return (
    <FAQItem data-open={open}>
      <FAQButton onClick={handleToggle} aria-expanded={open}>
        <span>{q}</span>
        <FAQIcon $open={open} aria-hidden>
          +
        </FAQIcon>
      </FAQButton>
      <FAQAnswer $open={open}>{a}</FAQAnswer>
    </FAQItem>
  );
};

type PricingExtrasWeb24Props = {
  user?: User;
};

const PricingExtrasWeb24: React.FC<PricingExtrasWeb24Props> = ({ user }) => {
  useEffect(() => {
    amplitude.track("web24_pricing_extras_viewed");
  }, []);

  const testimonials = getTestimonialsForGender(user?.gender as Gender | undefined);
  const transformationsRef = useSectionInView<HTMLElement>({ section: "transformations" });
  const featuredRef = useSectionInView<HTMLElement>({ section: "as_featured_in" });
  const faqRef = useSectionInView<HTMLElement>({ section: "faq" });
  const reviewsRef = useSectionInView<HTMLElement>({ section: "reviews" });

  return (
    <>
      <Section ref={transformationsRef}>
        <SectionInner>
          <SectionHeading>Results From Others Who Started Right Here</SectionHeading>
          <TestimonialGrid>
            {testimonials.map((t) => (
              <TestimonialCard key={t.id}>
                <BeforeAfterWrapper>
                  <BeforeAfterImage src={t.image} alt={`${t.name} before and after`} loading="lazy" />
                  <BeforeLabel>Before</BeforeLabel>
                  <AfterLabel>After</AfterLabel>
                </BeforeAfterWrapper>
                <CardBody>
                  <NameRow>
                    <Name>{t.name}</Name>
                    <QuoteIcon />
                  </NameRow>
                  <Headline>{t.headline}</Headline>
                  <QuoteText>{t.quote}</QuoteText>
                </CardBody>
              </TestimonialCard>
            ))}
          </TestimonialGrid>
        </SectionInner>
      </Section>

      <PressSection ref={featuredRef}>
        <SectionInner>
          <PressLabel>As Featured In</PressLabel>
          <PressRow>
            {PRESS_LOGOS.map((logo) => (
              <PressLogo key={logo.alt} src={logo.src} alt={logo.alt} loading="lazy" />
            ))}
          </PressRow>
        </SectionInner>
      </PressSection>

      <Section ref={faqRef}>
        <SectionInner>
          <SectionHeading>People Often Ask</SectionHeading>
          <FAQList>
            {FAQS.map((f, i) => (
              <FAQItemView key={f.q} q={f.q} a={f.a} index={i} />
            ))}
          </FAQList>
        </SectionInner>
      </Section>

      <Section ref={reviewsRef} style={{ backgroundColor: "#fff" }}>
        <SectionInner>
          <SectionHeading>Trusted by 30,000+ Users</SectionHeading>
          <ReviewsGrid>
            {REVIEWS.map((r) => (
              <ReviewCard key={r.title}>
                <CardWatermark viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <path d="M7.17 6C4.87 6 3 7.87 3 10.17v6.66c0 .64.52 1.17 1.17 1.17h4.66c.64 0 1.17-.53 1.17-1.17V12.5c0-.64-.53-1.17-1.17-1.17H6.33a3.33 3.33 0 0 1 2.17-2.72c.55-.2.83-.8.64-1.35-.17-.51-.7-.81-1.25-.74A5.5 5.5 0 0 0 7.17 6Zm10 0c-2.3 0-4.17 1.87-4.17 4.17v6.66c0 .64.52 1.17 1.17 1.17h4.66c.64 0 1.17-.53 1.17-1.17V12.5c0-.64-.53-1.17-1.17-1.17h-2.5a3.33 3.33 0 0 1 2.17-2.72c.55-.2.83-.8.64-1.35-.17-.51-.7-.81-1.25-.74a5.5 5.5 0 0 0-.72.08Z" />
                </CardWatermark>
                <StarRow>
                  <Stars aria-label="5 out of 5 stars">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                        <path d="M12 2.5l2.9 6.26 6.88.63-5.2 4.58 1.55 6.73L12 17.25l-6.13 3.45 1.55-6.73L2.22 9.39l6.88-.63L12 2.5z" />
                      </Star>
                    ))}
                  </Stars>
                  <ReviewRating>5.0</ReviewRating>
                </StarRow>
                <ReviewTitle>{r.title}</ReviewTitle>
                <ReviewBody>{r.body}</ReviewBody>
                <ReviewAuthor>
                  <Avatar $color={r.accent}>{r.initials}</Avatar>
                  <AuthorMeta>
                    <AuthorName>{r.author}</AuthorName>
                    <AuthorBadge>
                      <VerifiedTick viewBox="0 0 24 24" aria-hidden>
                        <circle cx="12" cy="12" r="10" fill={BWS_BLUE} />
                        <path d="M7.5 12.3l3 3 6-6.3" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                      </VerifiedTick>
                      Verified BWS+ Member
                    </AuthorBadge>
                  </AuthorMeta>
                </ReviewAuthor>
              </ReviewCard>
            ))}
          </ReviewsGrid>
        </SectionInner>
      </Section>
    </>
  );
};

export default PricingExtrasWeb24;
